import React, { useMemo, useState } from "react";
import { Modal, Skeleton, Wizard } from "@picklebet/ui";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { useDepositLimits } from "@/hooks/firestore/user/useDepositLimits";
import { useLastUsedDepositMethod } from "../../hooks/useLastUsedDepositMethod";
import { DEPOSIT_METHODS } from "../../utils/registry";
import { partitionMethods, pickDefaultMethod } from "../../utils/methods";
import type {
  DepositMethodId,
  DestinationMethodDescriptor,
  SelectableMethodDescriptor,
} from "../../types";
import { useAvailableDepositMethods } from "../../hooks/useAvailableDepositMethods";
import { useDepositWizard } from "../../hooks/useDepositWizard";
import {
  useSkrillDepositReturn,
  type DepositReturnState,
} from "../../hooks/useSkrillDepositReturn";
import {
  DepositWizardContext,
  type DepositWizardContextState,
} from "../../DepositWizardContext";
import { PaymentMethodStep } from "./steps/payment-method/PaymentMethodStep";
import * as styles from "./DepositWizard.module.scss";
import { DepositSuccess } from "./blocks/deposit-success/DepositSuccess";

const { selectableMethods: SELECTABLE_DEPOSIT_METHODS } =
  partitionMethods(DEPOSIT_METHODS);

const WizardHeader = ({ backAction = true }: { backAction?: boolean }) => {
  const { submitting } = useDepositWizard();

  return (
    <Wizard.Header backAction={backAction} backDisabled={submitting}>
      <Modal.CloseAction disabled={submitting} />
    </Wizard.Header>
  );
};

type SettledDepositWizardProps = {
  selectableMethods: SelectableMethodDescriptor[];
  destinations: DestinationMethodDescriptor[];
  depositReturn: DepositReturnState;
  requiredAmount?: number;
  initialMethodId?: DepositMethodId;
  initialStep: string;
  close: () => void;
  warnBetslipLoss: boolean;
  closeOnSuccess: boolean;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
};

const SettledDepositWizard = ({
  selectableMethods,
  destinations,
  depositReturn,
  requiredAmount,
  initialMethodId,
  initialStep,
  close,
  warnBetslipLoss,
  closeOnSuccess,
  submitting,
  setSubmitting,
}: SettledDepositWizardProps) => {
  // the required amount is also what the field opens on, so `Req` returns the
  // user to the prefill they started from
  const [amount, setAmount] = useState(requiredAmount ?? 0);
  const [selectedMethodId, setSelectedMethodId] = useState<
    DepositMethodId | undefined
  >(initialMethodId);
  const [selectedCardId, setSelectedCardId] = useState<string>();

  // derived, defaulting to the preferred available method, never undefined
  // because the gate guarantees non-empty
  const selectedMethod =
    selectableMethods.find((method) => method.id === selectedMethodId) ??
    pickDefaultMethod(selectableMethods);

  const wizardState = useMemo(
    () =>
      ({
        amount,
        setAmount,
        requiredAmount,
        depositReturn,
        selectableMethods,
        destinations,
        selectedMethod,
        setSelectedMethodId,
        selectedCardId,
        setSelectedCardId,
        submitting,
        setSubmitting,
        close,
        warnBetslipLoss,
        closeOnSuccess,
      }) satisfies DepositWizardContextState,
    [
      amount,
      requiredAmount,
      depositReturn,
      selectableMethods,
      destinations,
      selectedMethod,
      selectedCardId,
      submitting,
      setSubmitting,
      close,
      warnBetslipLoss,
      closeOnSuccess,
    ],
  );

  return (
    <DepositWizardContext value={wizardState}>
      <Wizard.Root initialStep={initialStep}>
        <Wizard.Step name="deposit" title={selectedMethod.primary.title}>
          <WizardHeader />
          <selectedMethod.primary.Component />
        </Wizard.Step>

        <Wizard.Step name="deposit-method" title="Select Payment Method">
          <WizardHeader />
          <PaymentMethodStep />
        </Wizard.Step>

        {/* declared after `deposit-method` on purpose: Wizard derives slide
            direction, and the empty-history back() target, from declared
            (mount) order, so hoisting these would animate a forward push
            backwards */}
        {destinations.map(({ destination }) => (
          <Wizard.Step
            key={destination.name}
            name={destination.name}
            title={destination.title}
          >
            <WizardHeader />
            <destination.Component />
          </Wizard.Step>
        ))}

        {selectableMethods
          .flatMap((method) => method.steps ?? [])
          .map(({ name, title, Component, terminal }) => (
            <Wizard.Step key={name} name={name} title={title}>
              <WizardHeader backAction={!terminal} />
              <Component />
            </Wizard.Step>
          ))}

        {!closeOnSuccess && (
          <Wizard.Step name="deposit-success">
            <WizardHeader backAction={false} />
            <DepositSuccess />
          </Wizard.Step>
        )}
      </Wizard.Root>
    </DepositWizardContext>
  );
};

const deriveInitialMethod = (
  availableMethods: readonly SelectableMethodDescriptor[] | undefined,
  lastUsedMethodId: DepositMethodId | null,
): SelectableMethodDescriptor => {
  // methods haven't resolved yet
  if (!availableMethods?.length) {
    return (
      SELECTABLE_DEPOSIT_METHODS.find(
        (method) => method.id === lastUsedMethodId,
      ) ?? SELECTABLE_DEPOSIT_METHODS[0]
    );
  }

  return (
    availableMethods.find((method) => method.id === lastUsedMethodId) ??
    pickDefaultMethod(availableMethods)
  );
};

/**
 * Stands in for every phase that resolves before the wizard can mount. Sized to
 * the settled view so the modal doesn't jump when the data lands. Callers that
 * already know which methods are available pass them in.
 */
export const DepositWizardSkeleton = ({
  availableMethods,
}: {
  availableMethods?: readonly SelectableMethodDescriptor[];
}) => {
  const [lastUsedMethodId] = useLastUsedDepositMethod();
  const method = deriveInitialMethod(availableMethods, lastUsedMethodId);

  return (
    <div className={styles.loadingStep}>
      <Modal.Header>
        <Modal.Heading>
          <Skeleton size="xs" className={styles.titleSkeleton} />
        </Modal.Heading>
      </Modal.Header>

      <method.primary.Skeleton />
    </div>
  );
};

export const DepositWizard = ({
  initialAmountCents,
  close,
  warnBetslipLoss = false,
  closeOnSuccess = false,
  excludedMethods,
  submitting,
  setSubmitting,
}: {
  initialAmountCents?: number;
  close: () => void;
  warnBetslipLoss?: boolean;
  closeOnSuccess?: boolean;
  excludedMethods?: readonly DepositMethodId[];
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
}) => {
  const {
    selectableMethods,
    destinations,
    isSettled: methodsSettled,
  } = useAvailableDepositMethods(excludedMethods);
  const {
    isSettled: returnSettled,
    initialMethodId,
    initialStep,
    depositReturn,
  } = useSkrillDepositReturn(selectableMethods);

  const currency = useFiatCurrency();
  const { depositLimit, isLoading: limitsLoading } = useDepositLimits(currency);
  const [lastUsedMethodId] = useLastUsedDepositMethod();

  // a prefill below the minimum would open the wizard already invalid, so it
  // is raised to the minimum instead
  const prefillSettled = !initialAmountCents || !limitsLoading;
  const requiredAmount =
    initialAmountCents && initialAmountCents > 0
      ? Math.max(initialAmountCents, depositLimit?.minimumDepositAmount ?? 0) /
        100
      : undefined;

  // wait until settled so Wizard.Root mounts with the right step
  if (!methodsSettled || !returnSettled || !prefillSettled) {
    return <DepositWizardSkeleton availableMethods={selectableMethods} />;
  }

  // unreachable today: PayID and debit card are both gated on AUD, so a
  // destination can't be the only thing available. If an availability rule
  // ever breaks that, this beats a wizard stranded on a bodiless `deposit`
  if (selectableMethods.length === 0) {
    return (
      <p className={styles.noMethods}>
        No deposit methods are available for your account. Please contact
        support.
      </p>
    );
  }

  return (
    <SettledDepositWizard
      selectableMethods={selectableMethods}
      destinations={destinations}
      depositReturn={depositReturn}
      requiredAmount={requiredAmount}
      initialMethodId={initialMethodId ?? lastUsedMethodId ?? undefined}
      initialStep={initialStep}
      close={close}
      warnBetslipLoss={warnBetslipLoss}
      closeOnSuccess={closeOnSuccess}
      submitting={submitting}
      setSubmitting={setSubmitting}
    />
  );
};
